import type { QuestionActor, QuestionContext } from '@webapp/survey/src/machine/question-machine';

import type { ResponseEventParams } from '../../entities/response-machine';
import { excludeHiddenAnswers } from '../../machine/helpers/logic';
import type { AnswersRequest } from '../queries-api';

import { QuestionSimpleApi } from './common/simple';

export class DropdownListApi extends QuestionSimpleApi {
    public change({ answersActors, responseActor, id, params }: QuestionContext, evt: ResponseEventParams): void {
        const { value } = evt;
        if (params.dropdownType !== 'multi') {
            responseActor.send({ type: 'CHANGE', questionId: id, value });

            if (answersActors.length > 0) {
                answersActors.forEach((answersActor) => {
                    const a = answersActor.getSnapshot().context;
                    a.response.send({ type: 'SET', value: a.id === value ? value : null });
                });
            }
        } else {
            const isExceptionSelected = answersActors.some((answerActor) => {
                const a = answerActor.getSnapshot().context;
                return a.exception && a.response.getSnapshot().context.value;
            });
            if (isExceptionSelected) {
                answersActors.forEach((answerActor) => {
                    const a = answerActor.getSnapshot().context;
                    if (!a.exception) {
                        a.response.send({
                            type: 'SET',
                            value: false,
                        });
                    }
                });
            }
        }
    }

    public query(actor: QuestionActor): AnswersRequest {
        const { id, answersActors: answersActorsRaw, responseActor, params } = actor.getSnapshot().context;
        const { comment, value } = responseActor.getSnapshot().context;
        const answersActors = excludeHiddenAnswers(answersActorsRaw);

        if (params.dropdownType !== 'multi') {
            return {
                question: id,
                data: answersActors
                    .filter((a) => a.getSnapshot().context.id === value)
                    .map((answerActor) => {
                        const {
                            id,
                            value: [text],
                        } = answerActor.getSnapshot().context;
                        return { answer: id, bool: true, text };
                    }),
                comment,
            };
        } else {
            return {
                question: id,
                data: answersActors
                    .filter((a) => a.getSnapshot().context.response.getSnapshot().context.value)
                    .map((answerActor) => {
                        const {
                            id,
                            value: [value1],
                        } = answerActor.getSnapshot().context;
                        return { answer: id, bool: true, text: value1 };
                    }),
                comment,
            };
        }
    }

    public validate(context: QuestionContext): boolean {
        const {
            answersActors: answersActorsRaw,
            params: { maxLength, minLength, required, dropdownType },
        } = context;
        if (!required) return true;

        if (dropdownType !== 'multi') {
            return super.validate(context);
        }

        const haveMaxLength = typeof maxLength === 'number';
        const haveMinLength = typeof minLength === 'number';
        let answeredCount = 0;
        let haveException = false;
        let valid = true;
        const answersActors = excludeHiddenAnswers(answersActorsRaw);

        answersActors.forEach((actor) => {
            const { exception, response: repsActor } = actor.getSnapshot().context;
            const response = repsActor.getSnapshot().context;
            if (!response) return;

            const value = !!response.value;
            const valid = response.value;

            if (valid) {
                ++answeredCount;
            }

            if (exception && value) {
                haveException = true;
            }
        });

        if (haveException) return true;

        if (required) {
            valid = answeredCount > 0;
        }

        if (haveMinLength) {
            valid = valid && answeredCount >= minLength;
        }

        if (haveMaxLength) {
            valid = valid && answeredCount <= maxLength;
        }

        return valid;
    }
}
